import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Alert
} from '@mui/material';
import { Close, Delete, Image as ImageIcon } from '@mui/icons-material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import VoiceRecorder from './VoiceRecorder';
import ChangeRequestFormSection, {
  CHANGE_REQUEST_TYPE,
  emptyChangeRequest,
  formatChangeRequestDescription,
  validateChangeRequest
} from './ChangeRequestFormSection';
import { captureScreenshot } from '../../utils/screenshotCapture';

const FeedbackForm = ({ open, onClose, onSubmitted }) => {
  const { showToast } = useToast();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    type: '',
    priority: '',
    severity: '',
    description: ''
  });
  const [voiceBlob, setVoiceBlob] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [capturingScreenshot, setCapturingScreenshot] = useState(false);
  const [formOpenedAt, setFormOpenedAt] = useState(null);
  const [changeRequest, setChangeRequest] = useState(emptyChangeRequest);
  const [submissionResult, setSubmissionResult] = useState(null);
  const dialogRef = useRef(null);

  const isChangeRequest = formData.type === CHANGE_REQUEST_TYPE;

  useEffect(() => {
    if (open) {
      setFormOpenedAt((prev) => prev || new Date().toISOString());
    } else {
      // Reset form when dialog closes
      resetForm();
      setFormOpenedAt(null);
    }
  }, [open]);

  const resetForm = () => {
    setFormData({
      type: '',
      priority: '',
      severity: '',
      description: ''
    });
    setVoiceBlob(null);
    setAttachments([]);
    setScreenshot(null);
    if (screenshotPreview) {
      URL.revokeObjectURL(screenshotPreview);
      setScreenshotPreview(null);
    }
    setChangeRequest(emptyChangeRequest());
    setSubmissionResult(null);
  };

  const handleClose = () => {
    if (submitting) return;
    resetForm();
    onClose();
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (field === 'type' && value === CHANGE_REQUEST_TYPE && user) {
      setChangeRequest(prev => ({
        ...prev,
        requesterFirstName: prev.requesterFirstName || user.firstName || '',
        requesterLastName: prev.requesterLastName || user.lastName || '',
        email: prev.email || user.email || '',
        raisedAt: prev.raisedAt || formOpenedAt || new Date().toISOString()
      }));
    }
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    if (attachments.length + files.length > 5) {
      showToast('Maximum 5 files allowed', 'error');
      return;
    }
    
    const newAttachments = files.map(file => ({
      file,
      name: file.name,
      size: file.size
    }));
    
    setAttachments(prev => [...prev, ...newAttachments]);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleVoiceRecordingComplete = (blob) => {
    setVoiceBlob(blob);
  };

  const handleSubmit = async () => {
    let submitPayload = { ...formData };

    if (!formData.type) {
      showToast('Please select a feedback type', 'error');
      return;
    }

    if (isChangeRequest) {
      const validationError = validateChangeRequest(changeRequest);
      if (validationError) {
        showToast(validationError, 'error');
        return;
      }
      submitPayload = {
        ...submitPayload,
        priority: 'Medium',
        severity: 'Minor',
        description: formatChangeRequestDescription(changeRequest)
      };
    } else if (!formData.priority || !formData.severity || !formData.description) {
      showToast('Please fill in all required fields', 'error');
      return;
    } else if (formData.description.trim().length === 0) {
      showToast('Description cannot be empty', 'error');
      return;
    }

    try {
      setSubmitting(true);

      // Capture screenshot right before submitting (excludes the dialog)
      // Screenshot is mandatory, so we must capture it
      let screenshotFile = screenshot;
      if (!screenshotFile) {
        try {
          setCapturingScreenshot(true);
          // Try to capture with 15 second timeout (increased from 3 seconds)
          // This allows more time for slower devices or complex pages
          screenshotFile = await Promise.race([
            captureScreenshot('feedback-form-dialog'),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Screenshot timeout - the capture took too long. Please try again or ensure your browser is not blocking the operation.')), 15000))
          ]);
          if (screenshotFile) {
            setScreenshot(screenshotFile);
            const previewUrl = URL.createObjectURL(screenshotFile);
            setScreenshotPreview(previewUrl);
          } else {
            throw new Error('Screenshot capture returned null');
          }
        } catch (screenshotError) {
          console.error('Error capturing screenshot:', screenshotError);
          const errorMessage = screenshotError.message.includes('timeout') 
            ? screenshotError.message 
            : 'Failed to capture screenshot. This may be due to browser restrictions or a slow connection. Please try again.';
          showToast(errorMessage, 'error');
          setSubmitting(false);
          setCapturingScreenshot(false);
          return; // Don't submit without screenshot
        } finally {
          setCapturingScreenshot(false);
        }
      }

      // Ensure we have a screenshot before submitting
      if (!screenshotFile) {
        showToast('Screenshot is required to submit feedback', 'error');
        setSubmitting(false);
        return;
      }

      // Create FormData
      const formDataToSend = new FormData();
      formDataToSend.append('type', submitPayload.type);
      formDataToSend.append('priority', submitPayload.priority);
      formDataToSend.append('severity', submitPayload.severity);
      formDataToSend.append('description', submitPayload.description);
      if (isChangeRequest) {
        formDataToSend.append('changeRequestDetails', JSON.stringify(changeRequest));
      }

      // Add voice recording
      if (voiceBlob) {
        formDataToSend.append('voiceRecording', voiceBlob, 'voice-recording.webm');
      }

      // Add attachments
      attachments.forEach((attachment, index) => {
        formDataToSend.append('attachments', attachment.file);
      });

      // Add screenshot
      if (screenshotFile) {
        formDataToSend.append('screenshot', screenshotFile);
      }

      // Submit feedback
      // Don't set Content-Type header - axios will set it automatically with boundary for FormData
      const response = await axios.post('/api/v1/feedback', formDataToSend);

      setSubmissionResult({
        feedbackId: response.data.feedbackId,
        shareableLink: response.data.shareableLink
      });

      if (isChangeRequest) {
        showToast(
          `Change request submitted (${response.data.feedbackId}). Pending Accountable Manager or Super Admin approval.`,
          'success'
        );
      } else {
        showToast(`Feedback submitted successfully! ID: ${response.data.feedbackId}`, 'success');
      }
      
      if (onSubmitted) {
        onSubmitted(response.data);
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      showToast(
        error.response?.data?.message || 'Failed to submit feedback',
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showToast('Copied to clipboard!', 'success');
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return;
        handleClose();
      }}
      maxWidth={isChangeRequest ? 'lg' : 'md'}
      fullWidth
      id="feedback-form-dialog"
      ref={dialogRef}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Report Feedback</Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {submissionResult ? (
          <Box sx={{ py: 2 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              Feedback submitted successfully!
            </Alert>
            <Typography variant="body1" gutterBottom>
              <strong>Feedback ID:</strong> {submissionResult.feedbackId}
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2">
                <strong>Shareable Link:</strong>
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  color: 'primary.main'
                }}
                onClick={() => copyToClipboard(window.location.origin + submissionResult.shareableLink)}
              >
                {submissionResult.shareableLink}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{ mt: 2 }}
            >
              Close
            </Button>
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={isChangeRequest ? 12 : 6}>
              <FormControl fullWidth required>
                <InputLabel>Feedback Type</InputLabel>
                <Select
                  value={formData.type}
                  onChange={handleInputChange('type')}
                  label="Feedback Type"
                >
                  <MenuItem value="New Feature">New Feature</MenuItem>
                  <MenuItem value="Recommendation">Recommendation</MenuItem>
                  <MenuItem value="I dont know">I dont know</MenuItem>
                  <MenuItem value="Bug">Bug (Defect/Error)</MenuItem>
                  <MenuItem value="Improvement">Improvement (Enhancement/Refactor)</MenuItem>
                  <MenuItem value="Task">Task</MenuItem>
                  <MenuItem value={CHANGE_REQUEST_TYPE}>{CHANGE_REQUEST_TYPE}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {!isChangeRequest && (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  onChange={handleInputChange('priority')}
                  label="Priority"
                >
                  <MenuItem value="Blocker">
                    <Tooltip title="Blocks all development work">
                      <span>Blocker</span>
                    </Tooltip>
                  </MenuItem>
                  <MenuItem value="Critical">
                    <Tooltip title="Critical issue requiring immediate attention">
                      <span>Critical</span>
                    </Tooltip>
                  </MenuItem>
                  <MenuItem value="High">
                    <Tooltip title="High priority issue">
                      <span>High</span>
                    </Tooltip>
                  </MenuItem>
                  <MenuItem value="Medium">
                    <Tooltip title="Medium priority issue">
                      <span>Medium</span>
                    </Tooltip>
                  </MenuItem>
                  <MenuItem value="Low">
                    <Tooltip title="Low priority issue">
                      <span>Low</span>
                    </Tooltip>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            )}
            {!isChangeRequest && (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Severity</InputLabel>
                <Select
                  value={formData.severity}
                  onChange={handleInputChange('severity')}
                  label="Severity"
                >
                  <MenuItem value="Critical">Critical</MenuItem>
                  <MenuItem value="Major">Major</MenuItem>
                  <MenuItem value="Minor">Minor</MenuItem>
                  <MenuItem value="Trivial">Trivial</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            )}
            {isChangeRequest ? (
              <Grid item xs={12}>
                <ChangeRequestFormSection
                  value={changeRequest}
                  onChange={setChangeRequest}
                  currentUser={user}
                />
              </Grid>
            ) : (
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                multiline
                rows={4}
                label="Description"
                value={formData.description}
                onChange={handleInputChange('description')}
                placeholder="Describe your feedback, issue, or suggestion..."
              />
            </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Voice Recording
              </Typography>
              <VoiceRecorder onRecordingComplete={handleVoiceRecordingComplete} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Attachments (Max 5 files, 5MB each)
              </Typography>
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                style={{ display: 'none' }}
                id="file-upload"
              />
              <label htmlFor="file-upload">
                <Button variant="outlined" component="span" size="small">
                  Choose Files
                </Button>
              </label>
              {attachments.length > 0 && (
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {attachments.map((attachment, index) => (
                    <Chip
                      key={index}
                      label={`${attachment.name} (${(attachment.size / 1024).toFixed(1)} KB)`}
                      onDelete={() => removeAttachment(index)}
                      deleteIcon={<Delete />}
                    />
                  ))}
                </Box>
              )}
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Screenshot (Auto-captured)
              </Typography>
              {screenshotPreview && (
                <Box sx={{ mt: 1 }}>
                  <img
                    src={screenshotPreview}
                    alt="Screenshot preview"
                    style={{ maxWidth: '100%', maxHeight: '200px', border: '1px solid #ccc', borderRadius: '4px' }}
                  />
                </Box>
              )}
            </Grid>
          </Grid>
        )}
      </DialogContent>
      {!submissionResult && (
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting || capturingScreenshot}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || capturingScreenshot}
          >
            {capturingScreenshot ? 'Capturing Screenshot...' : submitting ? 'Submitting...' : 'Report Feedback'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default FeedbackForm;

